import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PostGrid } from './PostGrid';
import { Reorder } from 'framer-motion';
import { DraggableTab } from './DraggableTab';
import { Leaderboards } from './Leaderboards';
import { TrophyIcon, BookmarkIcon, SettingsIcon, FilterIcon, XIcon, SendIcon, BellIcon } from './Icons';
import { stickerTextStyle } from './StickerText';
import { updateUserOnServer, sendSystemBroadcast } from '../api';
import { GlobalPostModal } from './GlobalPostModal';
import { useScrollLock } from '../hooks/useScrollLock';
import { Share } from '@capacitor/share';
import type { HydratedPost, UserData, PageState, Achievement, DailyAchievements, CompletedAchievement, PostType, Post } from '../types';

export interface FootprintPageProps {
    posts: HydratedPost[];
    onPostClick: (post: HydratedPost, contextList?: HydratedPost[]) => void;
    onLike: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number, post?: HydratedPost) => void;
    onNavigateToProfile?: (userId: string) => void;
    onHoldProfile?: (userId: string) => void;
    onNavigateToHashtag?: (tag: string) => void;
    onNavigateToCommunity?: (communityId: string) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    galleryAnimation: string;
    postBlurOpacity: number;
    preferredColumns?: 1 | 2 | 3 | 4 | 5 | 6;
    onColumnsChange?: (cols: 1 | 2 | 3 | 4 | 5 | 6) => void;
    preferredScale?: number;
    onScaleChange?: (scale: number) => void;
    postcardGap?: number;
    currentUserData: UserData;
    
    allUsers?: Record<string, UserData>;
    currentUserId?: string;
    isAdmin?: boolean;
    onOpenAdminSettings?: () => void;
    tabsOrder?: string[];
    onReorderTabs?: (newOrder: string[]) => void;
    setPage?: (page: PageState) => void;

    // Task container & modals
    achievements?: Achievement[];
    dailyAchievements?: DailyAchievements;
    taskId?: number;
    isDemo?: boolean;
    onCompleteAchievement?: (achievement: Achievement, postData: any) => void;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    closeInternalModalsTrigger?: number;
    onUploadFailed?: (localId: number) => void;
    params?: any;
    onPostDragProgress?: (post: any, progress: number, direction: 'left' | 'right' | null) => void;
    
    // For GlobalPostModal
    communities?: any[];
    conversations?: any[];
    onCreatePost?: (data: any) => any;
}

const getPostCycleDate = (timestamp?: number) => {
    if (!timestamp) return '';
    try {
        const d = new Date(timestamp);
        const hour = d.getHours();
        const cycleDate = new Date(d);
        if (hour < 12) {
            cycleDate.setDate(cycleDate.getDate() - 1);
        }
        return cycleDate.getFullYear() + '-' +
            String(cycleDate.getMonth() + 1).padStart(2, '0') + '-' +
            String(cycleDate.getDate()).padStart(2, '0');
    } catch (e) {
        return '';
    }
};

export const FootprintPage = ({
    posts,
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onNavigateToProfile,
    onHoldProfile,
    onNavigateToHashtag,
    onNavigateToCommunity,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    galleryAnimation,
    postBlurOpacity,
    preferredColumns,
    onColumnsChange,
    preferredScale,
    onScaleChange,
    postcardGap,
    currentUserData,
    allUsers = {},
    currentUserId = '',
    isAdmin = false,
    onOpenAdminSettings,
    tabsOrder = [],
    onReorderTabs,
    setPage,
    achievements = [],
    dailyAchievements,
    taskId,
    isDemo,
    onCompleteAchievement,
    onInternalModalStateChange,
    closeInternalModalsTrigger,
    onUploadFailed,
    params,
    onPostDragProgress,
    communities = [],
    conversations = [],
    onCreatePost
}: FootprintPageProps) => {
    const effectiveTaskId = taskId ?? (params as any)?.taskId;

    const initialTab = (currentUserData?.footprintTabsOrder?.[0] as any) || 'all';
    const [footTab, setFootTab] = useState<'today' | 'week' | 'connections' | 'all' | 'leaderboard'>(initialTab);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [taskToComplete, setTaskToComplete] = useState<Achievement | null>(null);
    
    useScrollLock(!!taskToComplete);

    useEffect(() => {
        onInternalModalStateChange?.(!!taskToComplete);
    }, [!!taskToComplete, onInternalModalStateChange]);

    useEffect(() => {
        if (effectiveTaskId) {
            setFootTab('all');
        }
    }, [effectiveTaskId]);

    const currentFootprintStmt = allUsers['goodlyfe']?.footprintStatement || 'The world is yours.';
    const [footprintStmtInput, setFootprintStmtInput] = useState(currentFootprintStmt);
    const isAllowedToEdit = currentUserId === 'goodlyfe' || isAdmin;

    const todayStr = useMemo(() => getPostCycleDate(Date.now()), []);
    const weekAgoStr = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return getPostCycleDate(d.getTime());
    }, []);

    const footprintPosts = useMemo(() => {
        return posts.filter(p => (p.type as string) === 'footprint' || p.footprintDetails || p.originalPost?.footprintDetails);
    }, [posts]);

    const filteredPosts = useMemo(() => {
        let result = footprintPosts;
        
        if (effectiveTaskId) {
            result = result.filter(post => String(post.footprintDetails?.achievementId) === String(effectiveTaskId));
        } else {
            switch (footTab) {
                case 'today':
                    result = footprintPosts.filter(p => getPostCycleDate(new Date(p.createdAt || 0).getTime()) === todayStr || p.uploadFailed);
                    break;
                case 'week':
                    result = footprintPosts.filter(p => getPostCycleDate(new Date(p.createdAt || 0).getTime()) >= weekAgoStr || p.uploadFailed);
                    break;
                case 'connections': {
                    const cur = currentUserData;
                    if (!cur) {
                        result = [];
                        break;
                    }
                    const set = new Set<string>();
                    (cur.following || []).forEach(u => set.add(u.toLowerCase()));
                    (cur.followers || []).forEach(u => set.add(u.toLowerCase()));
                    (cur.friends || []).forEach(u => set.add(u.toLowerCase()));
                    result = footprintPosts.filter(p => set.has(p.author.toLowerCase()) || p.uploadFailed);
                    break;
                }
                case 'all':
                case 'leaderboard':
                default:
                    result = footprintPosts;
                    break;
            }
        }

        const sorted = [...result].sort((a, b) => {
            if (a.uploadFailed && !b.uploadFailed) return -1;
            if (!a.uploadFailed && b.uploadFailed) return 1;
            return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        });

        return sorted;
    }, [footTab, footprintPosts, currentUserData, todayStr, weekAgoStr, effectiveTaskId]);

    const completedTodayAchievement = currentUserData?.completedAchievements?.find((c: any) => String(c.achievementId) === String(dailyAchievements?.daily?.id));

    const AchievementCard = ({ achievement, onCompleteClick, completedTodayAchievement }: { achievement: Achievement, onCompleteClick: (achievement: Achievement) => void, completedTodayAchievement: CompletedAchievement | undefined }) => {
        if (!achievement) return null;

        const completedList = currentUserData?.completedAchievements || [];
        const isThisCompleted = completedList.some((c: any) => String(c.achievementId) === String(achievement.id)) || (completedTodayAchievement && String(completedTodayAchievement.achievementId) === String(achievement.id));
        const isAnotherCompleted = !!completedTodayAchievement && !isThisCompleted;

        const appThemeColor = currentUserData?.appThemeColor || '#10b981';
        const appTextColor = currentUserData?.appTextColor || '#ffffff';
        const themeOpacity = currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50;

        const hexToRgba = (hex: string, op: number) => {
            const hexColor = hex.replace('#', '');
            let r = 0, g = 0, b = 0;
            if (hexColor.length === 3) {
                r = parseInt(hexColor.charAt(0) + hexColor.charAt(0), 16);
                g = parseInt(hexColor.charAt(1) + hexColor.charAt(1), 16);
                b = parseInt(hexColor.charAt(2) + hexColor.charAt(2), 16);
            } else if (hexColor.length === 6) {
                r = parseInt(hexColor.substring(0, 2), 16);
                g = parseInt(hexColor.substring(2, 4), 16);
                b = parseInt(hexColor.substring(4, 6), 16);
            } else return hex;
            return `rgba(${r}, ${g}, ${b}, ${op / 100})`;
        };

        const bgColor = hexToRgba(appThemeColor, themeOpacity);
        const borderColor = hexToRgba(appThemeColor, Math.min(100, themeOpacity + 40));

        const displayPoints = dailyAchievements?.daily?.id === achievement.id
            ? achievement.points
            : Math.round(achievement.points * 0.2);

        return (
            <div className={`p-6 rounded-2xl border flex flex-col`} style={{ backgroundColor: bgColor, borderColor: borderColor }}>
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-xl font-bold mt-1 text-raised" style={{ color: appTextColor, ...stickerTextStyle(0.18) }}>{achievement.title}</h3>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-lg font-bold text-raised" style={{ color: appTextColor, ...stickerTextStyle(0.18) }}>
                            {displayPoints} pts
                        </div>
                        <button
                            onClick={async (e) => {
                                e.stopPropagation();
                                const shareData = {
                                    title: 'GoodLyfe Footprint Task',
                                    text: `Check out this task on GoodLyfe: ${achievement.title} (${displayPoints} pts) - ${achievement.description}`,
                                    url: `https://goodlyfe.net/app?page=footprint&taskId=${achievement.id}`
                                };

                                try {
                                    await Share.share(shareData);
                                } catch (err) {
                                    console.error('Capacitor share failed, trying fallback', err);
                                    if (navigator.share) {
                                        navigator.share(shareData).catch(console.error);
                                    } else if (navigator.clipboard && navigator.clipboard.writeText) {
                                        navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
                                        alert('Link copied to clipboard!');
                                    } else {
                                        const text_area = document.createElement("textarea");
                                        text_area.value = `${shareData.text}\n${shareData.url}`;
                                        document.body.appendChild(text_area);
                                        text_area.focus();
                                        text_area.select();
                                        try {
                                            document.execCommand('copy');
                                            alert('Link copied to clipboard!');
                                        } catch (e) {
                                            console.error('Fallback: Oops, unable to copy', e);
                                            alert('Unable to copy link to clipboard. Share URL: ' + shareData.url);
                                        }
                                        document.body.removeChild(text_area);
                                    }
                                }
                            }}
                            className="p-1.5 rounded-full bg-black/20 hover:bg-black/40 transition-colors flex-shrink-0"
                            aria-label="Share Task"
                        >
                            <SendIcon className="w-5 h-5" style={{ color: appTextColor }} />
                        </button>
                        {isAdmin && (
                            <button
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Send push notification broadcast for task: ${achievement.title}?`)) {
                                        try {
                                            const text = `Daily Footprint Task: ${achievement.title} (${displayPoints} pts) - ${achievement.description}`;
                                            await sendSystemBroadcast(currentUserId, text);
                                            alert('Broadcast sent successfully!');
                                        } catch (error) {
                                            console.error('Failed to send broadcast', error);
                                            alert('Failed to send broadcast.');
                                        }
                                    }
                                }}
                                className="p-1.5 rounded-full bg-blue-600/20 hover:bg-blue-600/40 transition-colors flex-shrink-0"
                                aria-label="Broadcast Task"
                            >
                                <BellIcon className="w-5 h-5 text-blue-500" />
                            </button>
                        )}
                    </div>
                </div>
                <p className="mt-2 flex-grow text-raised" style={{ color: appTextColor, opacity: 0.8, ...stickerTextStyle(0.18) }}>{achievement.description}</p>
                <button
                    onClick={() => {
                        if (isDemo) {
                            alert("Completing achievements is disabled in Play Around mode. Log in to earn points!");
                            return;
                        }
                        onCompleteClick(achievement);
                    }}
                    disabled={isThisCompleted || isAnotherCompleted}
                    className="mt-4 w-full font-bold py-2 px-6 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black/20 hover:bg-black/30 border border-white/10"
                    style={{ color: appTextColor, ...stickerTextStyle(0.18) }}
                >
                    {isThisCompleted ? 'Completed!' : isAnotherCompleted ? 'Daily Task Done' : 'Complete Task'}
                </button>
            </div>
        );
    };

    return (
        <div className="w-full h-full flex flex-col pt-6 pb-20 animate-fade-in overflow-x-hidden" style={{ isolation: 'isolate' }}>
            {createPortal(
                <GlobalPostModal
                    isOpen={!!taskToComplete}
                    onClose={() => setTaskToComplete(null)}
                    onCreatePost={taskToComplete && onCompleteAchievement ? (postData) => onCompleteAchievement(taskToComplete, postData) : (() => { })}
                    communities={communities}
                    conversations={conversations}
                    postBlurOpacity={postBlurOpacity}
                    achievements={achievements}
                    dailyAchievements={dailyAchievements || { date: '', daily: null } as DailyAchievements}
                    initialDestination="footprint"
                />,
                document.body
            )}

            <FootprintArchiveModal
                isOpen={isArchiveModalOpen}
                onClose={() => setIsArchiveModalOpen(false)}
                achievements={achievements}
                dailyAchievements={dailyAchievements}
                onNavigateToAchievement={(tid) => setPage && setPage({ type: 'mod', modId: 'vibeman1_footprint', params: { taskId: tid } })}
                currentUserData={currentUserData}
            />

            <div className="max-w-4xl mx-auto px-4 w-full">
                <div className="text-center mb-8 mt-8 p-6 rounded-2xl" style={{ backgroundColor: 'var(--app-theme-bg)' }}>
                    <div className="flex-grow overflow-y-auto">
                        <h1 className="text-4xl font-bold text-raised" style={stickerTextStyle(0.35)}>Daily Footprint</h1>
                        <div className="mt-4 flex justify-center items-center gap-6">
                            <div className="text-2xl font-bold text-raised text-yellow-400" style={stickerTextStyle(0.18)}>{currentUserData?.totalPoints || 0} Lyfe points</div>
                        </div>
                        <div className="mt-4 flex flex-col items-center justify-center gap-3 max-w-md mx-auto">
                            {isAllowedToEdit ? (
                                <div className="w-full flex flex-col gap-2">
                                    <textarea
                                        value={footprintStmtInput}
                                        onChange={(e) => setFootprintStmtInput(e.target.value)}
                                        className="w-full p-3 bg-black/30 text-white text-xs rounded-xl border border-white/10 focus:outline-none focus:ring-1 focus:ring-blue-500 text-center resize-y"
                                        placeholder="Edit daily footprint statement..."
                                        rows={3}
                                        style={stickerTextStyle(0.18)}
                                    />
                                    {footprintStmtInput !== currentFootprintStmt && (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    await updateUserOnServer('goodlyfe', { footprintStatement: footprintStmtInput });
                                                    alert('Footprint statement saved!');
                                                } catch (e) {
                                                    console.error('Failed to update footprint statement', e);
                                                    alert('Failed to update footprint statement');
                                                }
                                            }}
                                            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-full transition-colors self-center"
                                        >
                                            Save Statement
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <p className='text-xs text-raised text-white whitespace-pre-wrap' style={stickerTextStyle(0.18)}>
                                    {currentFootprintStmt}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex justify-center items-center gap-4 mb-8">
                    <button onClick={() => setFootTab('leaderboard')} className={`flex items-center gap-2 font-semibold py-2 px-5 rounded-full transition-colors ${footTab === 'leaderboard' ? 'bg-yellow-600 text-white' : 'bg-yellow-600/20 text-yellow-500 hover:bg-yellow-600/40'}`} style={stickerTextStyle(0.18)}>
                        <TrophyIcon className="w-5 h-5" />
                        <span>Leaderboard</span>
                    </button>

                    <button onClick={() => setIsArchiveModalOpen(true)} className="flex items-center gap-2 font-semibold py-2 px-5 rounded-full transition-colors bg-blue-600/20 text-blue-500 hover:bg-blue-600/40" style={stickerTextStyle(0.18)}>
                        <BookmarkIcon className="w-5 h-5" />
                        <span>Archive</span>
                    </button>

                    {isAdmin && onOpenAdminSettings && (
                        <button onClick={onOpenAdminSettings} className="flex items-center justify-center p-2 rounded-full transition-colors bg-blue-600/20 text-blue-500 hover:bg-blue-600/40" aria-label="Admin Settings" style={stickerTextStyle(0.18)}>
                            <SettingsIcon className="w-6 h-6" />
                        </button>
                    )}
                </div>

                <div className="mx-auto md:w-1/2">
                    {dailyAchievements?.daily && <AchievementCard achievement={dailyAchievements.daily} onCompleteClick={a => setTaskToComplete(a)} completedTodayAchievement={completedTodayAchievement} />}

                    {effectiveTaskId && (() => {
                        const filteredTask = achievements.find(a => String(a.id) === String(effectiveTaskId));
                        if (!filteredTask) return null;
                        return (
                            <div className="mt-8 border-t border-gray-700 pt-6 animate-fade-in">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-bold text-white flex items-center gap-2" style={stickerTextStyle(0.18)}>
                                        <FilterIcon className="w-5 h-5 text-blue-400" />
                                        Selected Task
                                    </h2>
                                    <button
                                        onClick={() => setPage && setPage({ type: 'mod', modId: 'vibeman1_footprint' })}
                                        className="text-sm font-medium text-red-400 hover:text-red-300 transition-colors flex items-center gap-1 bg-red-500/10 hover:bg-red-500/20 px-3 py-1.5 rounded-full"
                                        style={stickerTextStyle(0.18)}
                                    >
                                        <XIcon className="w-4 h-4" /> Clear Filter
                                    </button>
                                </div>
                                <AchievementCard
                                    achievement={filteredTask}
                                    onCompleteClick={a => {
                                        const completed = currentUserData?.completedAchievements || [];
                                        if (completed.some((c: any) => String(c.achievementId) === String(a.id))) {
                                            alert('You have already completed this footprint task!');
                                            return;
                                        }
                                        setTaskToComplete(a);
                                    }}
                                    completedTodayAchievement={completedTodayAchievement}
                                />
                            </div>
                        );
                    })()}
                </div>

                {isReorderMode && (
                    <div 
                        className="fixed inset-0 z-40 bg-transparent" 
                        onClick={() => setIsReorderMode(false)}
                        onTouchStart={() => setIsReorderMode(false)}
                    />
                )}
                <div className={`w-full px-4 lg:px-[5%] py-2 mt-2 ${isReorderMode ? 'relative z-50' : ''}`}>
                    <nav
                        className="flex space-x-6 overflow-x-auto no-scrollbar py-1"
                        onTouchStart={(e) => e.stopPropagation()}
                        style={{ touchAction: isReorderMode ? 'none' : 'pan-x' }}
                    >
                        {(() => {
                            const baseTabs = [
                                { id: 'all', name: 'All' },
                                { id: 'today', name: 'Today' },
                                { id: 'week', name: 'This Week' },
                                { id: 'connections', name: currentUserData?.followerNickname || 'Connections' },
                                { id: 'leaderboard', name: 'Leaderboard' }
                            ];

                            let orderedTabs = baseTabs;
                            if (tabsOrder && tabsOrder.length > 0) {
                                orderedTabs = [];
                                const baseTabsMap = new Map(baseTabs.map(t => [t.id, t]));

                                tabsOrder.forEach(id => {
                                    if (baseTabsMap.has(id)) {
                                        orderedTabs.push(baseTabsMap.get(id)!);
                                        baseTabsMap.delete(id);
                                    }
                                });

                                baseTabsMap.forEach(t => orderedTabs.push(t));
                            }

                            return (
                                <Reorder.Group 
                                    axis="x" 
                                    values={orderedTabs.map(t => t.id)} 
                                    onReorder={(newOrder) => onReorderTabs && onReorderTabs(newOrder)} 
                                    className="flex space-x-6 h-full"
                                    style={{ overflow: 'visible' }}
                                >
                                    {orderedTabs.map(tab => (
                                        <DraggableTab
                                            key={tab.id}
                                            item={tab.id}
                                            label={tab.name}
                                            isActive={footTab === tab.id}
                                            onClick={() => setFootTab(tab.id as any)}
                                            color={tab.id === 'leaderboard' ? (footTab === tab.id ? '#eab308' : '#fef08a') : (currentUserData?.appTextColor || '#ffffff')}
                                            isReorderMode={isReorderMode}
                                            onEnterReorderMode={() => setIsReorderMode(true)}
                                            onExitReorderMode={() => setIsReorderMode(false)}
                                        />
                                    ))}
                                </Reorder.Group>
                            );
                        })()}
                    </nav>
                </div>
            </div>

            {footTab === 'leaderboard' ? (
                <div className="max-w-4xl mx-auto px-4 w-full flex-grow">
                    <Leaderboards
                        usersData={allUsers}
                        currentUserId={currentUserId}
                        onNavigateToProfile={onNavigateToProfile!}
                        onHoldProfile={onHoldProfile}
                    />
                </div>
            ) : (
                <div className="w-full relative flex-grow">
                    <PostGrid
                        key={footTab}
                        posts={filteredPosts}
                        onPostClick={onPostClick}
                        onLike={onLike}
                        onBump={onBump}
                        onBookmark={onBookmark}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onNavigateToProfile={onNavigateToProfile!}
                        onNavigateToHashtag={onNavigateToHashtag!}
                        onNavigateToCommunity={onNavigateToCommunity}
                        animatingLikes={animatingLikes}
                        animatingBumps={animatingBumps}
                        animatingBookmarks={animatingBookmarks}
                        galleryAnimation={galleryAnimation}
                        postBlurOpacity={postBlurOpacity}
                        preferredColumns={preferredColumns}
                        onColumnsChange={onColumnsChange}
                        preferredScale={preferredScale}
                        onScaleChange={onScaleChange}
                        postcardGap={postcardGap}
                        showEndMessage={footTab === 'connections'}
                        onPostDragProgress={onPostDragProgress}
                    />
                </div>
            )}
        </div>
    );
};

export const FootprintArchiveModal = ({
    isOpen,
    onClose,
    dailyAchievements,
    onNavigateToAchievement,
    currentUserData
}: {
    isOpen: boolean;
    onClose: () => void;
    achievements?: any[];
    dailyAchievements: any;
    onNavigateToAchievement: (id: number) => void;
    currentUserData?: UserData;
}) => {
    const [archivedItems, setArchivedItems] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(59, 130, 246, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/g, `${opacityVal})`);
        if (color.startsWith('#')) {
            const hex = color.replace('#', '');
            let r = 0, g = 0, b = 0;
            if (hex.length === 3) {
                r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
                g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
                b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
            } else if (hex.length === 6) {
                r = parseInt(hex.substring(0, 2), 16);
                g = parseInt(hex.substring(2, 4), 16);
                b = parseInt(hex.substring(4, 6), 16);
            } else return color;
            return `rgba(${r}, ${g}, ${b}, ${opacityVal})`;
        }
        return color;
    };
    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    useEffect(() => {
        if (!isOpen) return;
        setIsLoading(true);
        import('axios').then(axios => {
            import('../api').then(({ API_URL }) => {
                const token = localStorage.getItem('token');
                const url = `${API_URL}achievements`;
                axios.default.get(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
                    .then(res => {
                        if (res.data) {
                            const items = res.data.filter((ach: any) => ach.isArchived);
                            setArchivedItems(items);
                        }
                    })
                    .catch(err => console.error('[Archive Modal] Failed to fetch:', err))
                    .finally(() => setIsLoading(false));
            });
        });
    }, [isOpen]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[150] p-4 animate-fade-in" onClick={onClose}>
            <div className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col max-h-[80vh] shadow-lg p-6 backdrop-blur-md" style={{ backgroundColor: modalBg, color: userTextColor }} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <h2 className="text-xl font-bold" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>Footprint Archive</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-grow overflow-y-auto min-h-0 -mx-6 px-6 allow-scroll">
                    <div className="space-y-2">
                        {isLoading ? (
                            <div className="text-center text-gray-400 py-10">Loading archived tasks...</div>
                        ) : archivedItems.length === 0 ? (
                            <div className="text-center text-gray-400 py-10 flex flex-col items-center justify-center space-y-4">
                                <p>No archived tasks yet.</p>
                            </div>
                        ) : (
                            archivedItems.map((ach, index) => {
                                const isDaily = dailyAchievements?.daily?.id === ach.id;
                                const displayPoints = isDaily ? ach.points : Math.round(ach.points * 0.2);
                                return (
                                    <div
                                        key={ach.id || index}
                                        onClick={() => { onNavigateToAchievement(ach.id); onClose(); }}
                                        className={`w-full flex items-center gap-4 p-3 rounded-xl hover:bg-white/10 transition-all cursor-pointer border ${
                                            isDaily 
                                                ? 'bg-blue-600/15 border-blue-500/35 hover:bg-blue-600/25' 
                                                : 'bg-gray-800/40 border-transparent hover:bg-gray-800/60'
                                        }`}
                                    >
                                        <div className="flex-grow min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-white text-base truncate">{ach.title}</span>
                                                {isDaily && (
                                                    <span className="bg-yellow-500/20 text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-yellow-500/30">
                                                        DAILY TASK
                                                    </span>
                                                )}
                                                <span className="bg-gray-500/20 text-gray-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-gray-500/30">
                                                    ARCHIVED
                                                </span>
                                            </div>
                                            {ach.requiredAttachmentType && (
                                                <span className="bg-red-500/25 border border-red-500/30 text-red-400 text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 mt-1 inline-block">
                                                    ⚠️ {ach.requiredAttachmentType.toUpperCase().replace('_', ' ')}
                                                </span>
                                            )}
                                            <p className="text-sm text-gray-400 mt-1 truncate">{ach.description}</p>
                                        </div>

                                        <div className="text-right flex-shrink-0">
                                            <span className="font-bold text-yellow-400 text-base">{displayPoints} pts</span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};
